import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, MenuItem, Pagination, Paper, Tab, Tabs, TextField, Typography } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { apiFetch } from '../services/api';
import { useLogowanie } from '../context/KontekstLogowania';


function MiniWykresAmortyzacji({harmonogram, wartoscPoczatkowa}) {
  const dane=[{v:Number(wartoscPoczatkowa||0)},...(harmonogram||[]).map(h=>({v:Number(h.wartosc_netto||0)}))];
  if(dane.length<2) return <Typography variant="body2" color="text.secondary">Brak danych do wykresu.</Typography>;
  const max=Math.max(...dane.map(x=>x.v),1), w=240, h=110, pad=8;
  const punkty=dane.map((x,i)=>{
    const px=pad+(i/(dane.length-1))*(w-pad*2);
    const py=pad+(1-x.v/max)*(h-pad*2);
    return `${px},${py}`;
  }).join(' ');
  return <Box>
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="118" role="img" aria-label="Mini wykres wartości netto środka w czasie">
      <line x1={pad} y1={h-pad} x2={w-pad} y2={h-pad} stroke="currentColor" opacity="0.15"/>
      <polyline fill="none" stroke="currentColor" strokeWidth="2.5" points={punkty}/>
    </svg>
    <Box sx={{display:'flex',justifyContent:'space-between',gap:1}}>
      <Typography variant="caption" color="text.secondary">Start</Typography>
      <Typography variant="caption" color="text.secondary">Koniec planu</Typography>
    </Box>
  </Box>;
}

export default function SzczegolySrodka() {
  const { id } = useParams();
  const { maUprawnienie } = useLogowanie();
  const [srodek, setSrodek] = useState(null);
  const [planAmortyzacji, setPlanAmortyzacji] = useState(null);
  const [harmonogramAmortyzacji, setHarmonogramAmortyzacji] = useState([]);
  const [stanAmortyzacji, setStanAmortyzacji] = useState(null);
  const [widokAmortyzacji, setWidokAmortyzacji] = useState('HISTORIA');
  const [stronaAmortyzacji, setStronaAmortyzacji] = useState(1);
  const [tab, setTab] = useState(0);
  const [przesuniecia, setPrzesuniecia] = useState([]);
  const [lokalizacje, setLokalizacje] = useState([]);
  const [pracownicy, setPracownicy] = useState([]);
  const [open, setOpen] = useState(false);
  const [openUsun, setOpenUsun] = useState(false);
  const [powodUsuniecia, setPowodUsuniecia] = useState('');
  const [wniosekUsuniecia, setWniosekUsuniecia] = useState(null);
  const [filtrNotatek, setFiltrNotatek] = useState('WSZYSTKIE');
  const [szukajNotatek, setSzukajNotatek] = useState('');
  const [stronaNotatek, setStronaNotatek] = useState(1);
  const NOTATKI_NA_STRONE = 6;
  const [bladUsuniecia, setBladUsuniecia] = useState('');
  const [ruch, setRuch] = useState({ id_lokalizacji_do:'', id_pracownika_do:'', powod:'PRZENIESIENIE', komentarz:'' });

  const load = async () => {
    const [a,m] = await Promise.all([apiFetch(`/srodki-trwale/${id}`), apiFetch(`/przesuniecia/${id}`)]);
    setSrodek(a); setPrzesuniecia(m);
    if(maUprawnienie('DEPRECIATION_VIEW')){
      try{
        const ar=await apiFetch(`/amortyzacja/srodki/${id}`);
        setPlanAmortyzacji(ar.plan||null);
        setHarmonogramAmortyzacji(ar.harmonogram||[]);
        setStanAmortyzacji(ar.stan_na_dzis||null);
      }catch(_e){
        setPlanAmortyzacji(null);setHarmonogramAmortyzacji([]);setStanAmortyzacji(null);
      }
    }
    try { setWniosekUsuniecia(await apiFetch(`/srodki-trwale/${id}/wniosek-usuniecia`)); } catch { setWniosekUsuniecia(null); }
  };

  useEffect(() => {
    load();
    Promise.all([apiFetch('/slowniki/lokalizacje'),apiFetch('/slowniki/pracownicy')]).then(([l,p])=>{setLokalizacje(l);setPracownicy(p);});
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const przesun = async () => {
    await apiFetch(`/przesuniecia/${id}`, {
      method:'POST',
      body:JSON.stringify({
        id_lokalizacji_do:ruch.id_lokalizacji_do ? Number(ruch.id_lokalizacji_do) : null,
        id_pracownika_do:ruch.id_pracownika_do ? Number(ruch.id_pracownika_do) : null,
        powod:ruch.powod, komentarz:ruch.komentarz
      })
    });
    setOpen(false); await load();
  };

  const poprosOUsuniecie = async () => {
    setBladUsuniecia('');
    try {
      await apiFetch(`/srodki-trwale/${id}/wniosek-usuniecia`, {method:'POST',body:JSON.stringify({powod:powodUsuniecia})});
      setOpenUsun(false); setPowodUsuniecia(''); await load();
    } catch(e) { setBladUsuniecia(e.message); }
  };

  if (!srodek) return <CircularProgress/>;

  return <>
    <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
      <Box>
        <Typography variant="h4" fontWeight={700}>{srodek.nazwa_indeksu}</Typography>
      </Box>
      <Box sx={{display:'flex',gap:1}}>
        {maUprawnienie('ASSET_EDIT') && <Button component={Link} to={`/srodki-trwale/${id}/edytuj`} variant="outlined">Edytuj</Button>}
        {maUprawnienie('DEPRECIATION_MANAGE') && <Button component={Link} to={`/amortyzacja?srodek=${id}`} variant="outlined">Edytuj amortyzację</Button>}
        {maUprawnienie('ASSET_DELETE_REQUEST') && wniosekUsuniecia?.status!=='OCZEKUJE' &&
          <Button color="error" variant="outlined" onClick={()=>setOpenUsun(true)}>Poproś o usunięcie</Button>}
        {maUprawnienie('ASSET_MOVE') && <Button variant="contained" onClick={()=>setOpen(true)}>Przesuń / przekaż</Button>}
      </Box>
    </Box>

    {wniosekUsuniecia?.status==='OCZEKUJE' && <Alert severity="warning" sx={{mt:2}}>Dla tego środka oczekuje wniosek o trwałe usunięcie. Powód: {wniosekUsuniecia.powod}</Alert>}

    <Paper sx={{mt:2,borderRadius:3,border:'1px solid #e3eaf3',boxShadow:'0 8px 26px rgba(18,46,84,.055)',overflow:'hidden',bgcolor:'#fff'}}>
      <Tabs value={tab} onChange={(_,v)=>setTab(v)} variant="scrollable" scrollButtons="auto"
        sx={{px:2,borderBottom:'1px solid #edf1f6',bgcolor:'#fff'}}>
        <Tab label="Informacje"/><Tab label="Dane ewidencyjne"/><Tab label="Parametry techniczne"/><Tab label="Przesunięcia"/><Tab label="Notatki i historia"/><Tab label="Amortyzacja"/>
      </Tabs>

      <Box sx={{p:{xs:2,md:3}}}>
      {tab===0 && <Box>
        <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',lg:'1fr 1fr'},gap:2}}>
          <Paper variant="outlined" sx={{p:2,borderRadius:2}}>
            <Typography fontWeight={800} sx={{mb:1.5}}>Identyfikacja i urządzenie</Typography>
            {[
              ['Nr unikatowy',srodek.numer_unikatowy],['Nr INW',srodek.numer_inw],['Nr seryjny',srodek.numer_seryjny],
              ['Kategoria',srodek.kategoria],['Producent',srodek.producent],['Model',srodek.model]
            ].map(([l,v])=><Box key={l} sx={{display:'flex',justifyContent:'space-between',gap:2,py:.75,borderBottom:'1px solid #f0f3f7'}}>
              <Typography variant="body2" color="text.secondary">{l}</Typography><Typography variant="body2" fontWeight={700}>{v || '—'}</Typography>
            </Box>)}
          </Paper>

          <Paper variant="outlined" sx={{p:2,borderRadius:2}}>
            <Typography fontWeight={800} sx={{mb:1.5}}>Przypisanie i status</Typography>
            {[
              ['Lokalizacja',srodek.lokalizacja],['Użytkownik',srodek.uzytkownik],['Firma serwisowa',srodek.firma_serwisowa],['Status',srodek.status]
            ].map(([l,v])=><Box key={l} sx={{display:'flex',justifyContent:'space-between',gap:2,py:.75,borderBottom:'1px solid #f0f3f7'}}>
              <Typography variant="body2" color="text.secondary">{l}</Typography><Typography variant="body2" fontWeight={700}>{v || '—'}</Typography>
            </Box>)}
          </Paper>
        </Box>
        {srodek.komentarz && <Box sx={{mt:2,p:2,bgcolor:'#fffaf0',border:'1px solid #f2dfb7',borderRadius:2}}>
          <Typography variant="subtitle2" fontWeight={800}>Notatka - informacje ogólne</Typography>
          <Typography sx={{whiteSpace:'pre-wrap',mt:.5}}>{srodek.komentarz}</Typography>
        </Box>}
      </Box>}

      {tab===1 && <Box>
        <Typography variant="h6" fontWeight={850} sx={{mb:2}}>Dane ewidencyjne</Typography>
        <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',md:'repeat(3,minmax(0,1fr))'},gap:1.5}}>
          {[
            ['Data produkcji',srodek.data_produkcji,true],['Data zakupu',srodek.data_zakupu,true],
            ['Data przyjęcia',srodek.data_przyjecia,true],['Gwarancja do',srodek.data_gwarancji_do,true],
            ['Numer zamówienia',srodek.numer_zamowienia,false]
          ].map(([l,v,czyData])=><Box key={l} sx={{p:1.8,border:'1px solid #edf1f6',borderRadius:2,bgcolor:'#fbfdff'}}>
            <Typography variant="caption" color="text.secondary">{l}</Typography>
            <Typography fontWeight={750} sx={{mt:.25}}>{v ? (czyData ? String(v).slice(0,10) : String(v)) : '—'}</Typography>
          </Box>)}
        </Box>
        {srodek.notatka_ewidencja && <Box sx={{mt:2,p:2,bgcolor:'#fffaf0',border:'1px solid #f0deb9',borderRadius:2}}>
          <Typography variant="subtitle2" fontWeight={850}>Notatka - dane ewidencyjne</Typography>
          <Typography sx={{whiteSpace:'pre-wrap',mt:.5}}>{srodek.notatka_ewidencja}</Typography>
        </Box>}
      </Box>}

      {tab===2 && <Box>
        <Typography variant="h6" fontWeight={850} sx={{mb:2}}>Parametry techniczne</Typography>
        {srodek.parametry?.length
          ? <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',md:'repeat(2,minmax(0,1fr))'},gap:1.2}}>
              {srodek.parametry.map(p=><Box key={p.id_parametru} sx={{display:'flex',justifyContent:'space-between',gap:2,p:1.6,border:'1px solid #edf1f6',borderRadius:2,bgcolor:'#fbfdff'}}>
                <Typography variant="body2" color="text.secondary">{p.nazwa}</Typography>
                <Typography variant="body2" fontWeight={800}>{p.wartosc ?? '—'} {p.jednostka || ''}</Typography>
              </Box>)}
            </Box>
          : <Typography>Brak parametrów.</Typography>}
        {srodek.notatka_parametry && <Box sx={{mt:2,p:2,bgcolor:'#f5f0ff',border:'1px solid #ded2ff',borderRadius:2}}>
          <Typography variant="subtitle2" fontWeight={850}>Notatka - parametry techniczne</Typography>
          <Typography sx={{whiteSpace:'pre-wrap',mt:.5}}>{srodek.notatka_parametry}</Typography>
        </Box>}
      </Box>}

      {tab===3 && <Box>
        <Typography variant="h6" fontWeight={850} sx={{mb:2}}>Historia przesunięć</Typography>
        {przesuniecia.length ? przesuniecia.map(p=><Paper key={p.id_przesuniecia} variant="outlined" sx={{p:1.8,mb:1.2,borderRadius:2}}>
          <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',md:'130px 1fr 1fr 1fr'},gap:1.2,alignItems:'start'}}>
            <Box><Typography variant="caption" color="text.secondary">Data</Typography><Typography variant="body2" fontWeight={750}>{new Date(p.data_przesuniecia).toLocaleString()}</Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Lokalizacja</Typography><Typography variant="body2">{p.lokalizacja_z || '—'} → <b>{p.lokalizacja_do || '—'}</b></Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Użytkownik</Typography><Typography variant="body2">{p.uzytkownik_z || '—'} → <b>{p.uzytkownik_do || '—'}</b></Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Powód</Typography><Typography variant="body2" fontWeight={700}>{p.powod}</Typography></Box>
          </Box>
          {p.komentarz && <Box sx={{mt:1.2,p:1.2,bgcolor:'#f8fafc',borderRadius:1.5}}><Typography variant="body2"><b>Notatka:</b> {p.komentarz}</Typography></Box>}
          {p.wykonal_nazwa && <Typography variant="caption" color="text.secondary">Wykonał: {p.wykonal_nazwa}</Typography>}
        </Paper>) : <Typography>Brak przesunięć.</Typography>}
      </Box>}

      {tab===4 && <Box>
        <Typography variant="h6" fontWeight={700} sx={{ mb:0.5 }}>Historia notatek</Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb:2}}>
          Historia jest tworzona automatycznie przy dodaniu, zmianie lub usunięciu notatki. Wpisów historycznych nie edytuje się.
        </Typography>

        <Box sx={{display:'flex',gap:1,flexWrap:'wrap',mb:2}}>
          {[
            ['WSZYSTKIE','Wszystkie'],
            ['INFORMACJE','Informacje'],
            ['EWIDENCJA','Dane ewidencyjne'],
            ['PARAMETRY','Parametry']
          ].map(([kod,label])=>
            <Button
              key={kod}
              size="small"
              variant={filtrNotatek===kod ? 'contained' : 'outlined'}
              onClick={()=>{setFiltrNotatek(kod);setStronaNotatek(1);}}
            >
              {label}
            </Button>
          )}
        </Box>

        <TextField
          fullWidth
          size="small"
          label="Szukaj w historii notatek"
          placeholder="Treść, autor, typ lub akcja..."
          value={szukajNotatek}
          onChange={e=>{setSzukajNotatek(e.target.value);setStronaNotatek(1);}}
          sx={{mb:2}}
        />

        {(() => {
          const q = szukajNotatek.trim().toLowerCase();
          const wszystkie = (srodek.notatki || []).filter(n => {
            if(filtrNotatek!=='WSZYSTKIE' && n.typ_notatki!==filtrNotatek) return false;
            if(!q) return true;
            return [
              n.tresc,n.tresc_przed,n.tresc_po,n.autor_nazwa,n.typ_notatki,n.akcja
            ].some(v=>String(v||'').toLowerCase().includes(q));
          });

          const liczbaStron = Math.max(1, Math.ceil(wszystkie.length / NOTATKI_NA_STRONE));
          const bezpiecznaStrona = Math.min(stronaNotatek, liczbaStron);
          const od = (bezpiecznaStrona-1)*NOTATKI_NA_STRONE;
          const lista = wszystkie.slice(od, od+NOTATKI_NA_STRONE);

          const nazwaTypu = t => ({
            INFORMACJE:'Informacje',
            EWIDENCJA:'Dane ewidencyjne',
            PARAMETRY:'Parametry techniczne',
            DODATKOWA:'Dodatkowa'
          }[t] || t || 'Notatka');
          const nazwaAkcji = a => ({
            DODANO:'Dodano',
            ZMIENIONO:'Zmieniono',
            USUNIETO:'Usunięto'
          }[a] || a || 'Wpis');

          return <>
            {!lista.length && <Typography color="text.secondary">Brak wpisów spełniających kryteria.</Typography>}

            {lista.map(n=><Paper key={n.id_notatki} variant="outlined" sx={{p:2,mb:2}}>
              <Box sx={{display:'flex',justifyContent:'space-between',gap:2,flexWrap:'wrap',mb:1}}>
                <Typography fontWeight={700}>{nazwaTypu(n.typ_notatki)} · {nazwaAkcji(n.akcja)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {n.autor_nazwa || '—'} · {new Date(n.utworzono).toLocaleString()}
                </Typography>
              </Box>

              {n.akcja==='DODANO' && <Box>
                <Typography variant="caption" color="text.secondary">Dodana treść</Typography>
                <Typography sx={{whiteSpace:'pre-wrap'}}>{n.tresc_po || n.tresc || '—'}</Typography>
              </Box>}

              {n.akcja==='ZMIENIONO' && <Box sx={{display:'grid',gridTemplateColumns:'repeat(2,minmax(0,1fr))',gap:2}}>
                <Box sx={{p:1.5,bgcolor:'#fafafa',borderRadius:1}}>
                  <Typography variant="caption" color="text.secondary">Przed zmianą</Typography>
                  <Typography sx={{whiteSpace:'pre-wrap'}}>{n.tresc_przed || '—'}</Typography>
                </Box>
                <Box sx={{p:1.5,bgcolor:'#fafafa',borderRadius:1}}>
                  <Typography variant="caption" color="text.secondary">Po zmianie</Typography>
                  <Typography sx={{whiteSpace:'pre-wrap'}}>{n.tresc_po || '—'}</Typography>
                </Box>
              </Box>}

              {n.akcja==='USUNIETO' && <Box>
                <Typography variant="caption" color="text.secondary">Usunięta treść</Typography>
                <Typography sx={{whiteSpace:'pre-wrap'}}>{n.tresc_przed || n.tresc || '—'}</Typography>
              </Box>}

              {!['DODANO','ZMIENIONO','USUNIETO'].includes(n.akcja) && <Typography sx={{whiteSpace:'pre-wrap'}}>{n.tresc}</Typography>}
            </Paper>)}

            {wszystkie.length > NOTATKI_NA_STRONE && <Box sx={{display:'flex',alignItems:'center',justifyContent:'center',gap:2,mt:2}}>
              <Button
                size="small"
                variant="outlined"
                disabled={bezpiecznaStrona<=1}
                onClick={()=>setStronaNotatek(s=>Math.max(1,s-1))}
              >
                Poprzednia
              </Button>
              <Typography variant="body2">
                Strona {bezpiecznaStrona} z {liczbaStron} · wpisów: {wszystkie.length}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={bezpiecznaStrona>=liczbaStron}
                onClick={()=>setStronaNotatek(s=>Math.min(liczbaStron,s+1))}
              >
                Następna
              </Button>
            </Box>}
          </>;
        })()}
      </Box>}

      {tab===5 && <Box>
        <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:2,mb:2,flexWrap:'wrap'}}>
          <Box>
            <Typography variant="h6" fontWeight={900}>Amortyzacja</Typography>
            <Typography variant="body2" color="text.secondary">Bieżąca wartość środka, historia odpisów i pozostały harmonogram.</Typography>
          </Box>
          {maUprawnienie('DEPRECIATION_MANAGE')&&<Button component={Link} to={`/amortyzacja?srodek=${id}`} variant="contained">Edytuj amortyzację</Button>}
        </Box>

        {!planAmortyzacji?<Alert severity="info">Dla tego środka nie utworzono jeszcze planu amortyzacji.</Alert>:(()=>{
          const money=v=>Number(v||0).toLocaleString('pl-PL',{minimumFractionDigits:2,maximumFractionDigits:2})+' zł';
          const metoda=({LINIOWA:'Liniowa',DEGRESYWNA:'Degresywna',JEDNORAZOWA:'Jednorazowa'}[planAmortyzacji.metoda_kod]||planAmortyzacji.metoda_kod);
          const dzis=new Date(); dzis.setHours(23,59,59,999);
          const historia=harmonogramAmortyzacji.filter(h=>new Date(String(h.data_odpisu).slice(0,10)+'T00:00:00')<=dzis);
          const przyszle=harmonogramAmortyzacji.filter(h=>new Date(String(h.data_odpisu).slice(0,10)+'T00:00:00')>dzis);
          const dane=widokAmortyzacji==='HISTORIA'?[...historia].reverse():przyszle;
          const NA_STRONE=12;
          const strony=Math.max(1,Math.ceil(dane.length/NA_STRONE));
          const page=Math.min(stronaAmortyzacji,strony);
          const widoczne=dane.slice((page-1)*NA_STRONE,page*NA_STRONE);
          const wartosc=Number(planAmortyzacji.wartosc_poczatkowa||0);
          const umorzenie=Number(stanAmortyzacji?.umorzenie_biezace ?? historia.slice(-1)[0]?.umorzenie_narastajaco ?? 0);
          const netto=Number(stanAmortyzacji?.wartosc_netto_biezaca ?? historia.slice(-1)[0]?.wartosc_netto ?? wartosc);
          const postep=wartosc>0?Math.min(100,Math.max(0,(umorzenie/wartosc)*100)):0;
          const zakonczona=netto<=0.009;
          const start=String(planAmortyzacji.data_rozpoczecia||'—').slice(0,10);
          const koniec=harmonogramAmortyzacji.length?String(harmonogramAmortyzacji[harmonogramAmortyzacji.length-1].data_odpisu).slice(0,10):'—';

          return <>
            <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',sm:'repeat(2,minmax(0,1fr))',xl:'repeat(4,minmax(0,1fr))'},gap:1.5}}>
              {[
                ['Wartość początkowa',money(wartosc)],
                ['Umorzenie na dziś',money(umorzenie)],
                ['Bieżąca wartość netto',money(netto)],
                ['Status',zakonczona?'Zamortyzowany':'W trakcie']
              ].map(([l,v])=><Paper key={l} variant="outlined" sx={{p:1.7,borderRadius:2,borderColor:'#e3eaf3',bgcolor:l==='Bieżąca wartość netto'?'#f7faff':'#fff'}}>
                <Typography variant="caption" color="text.secondary">{l}</Typography>
                <Typography variant="h6" fontWeight={900}>{v}</Typography>
              </Paper>)}
            </Box>

            <Paper variant="outlined" sx={{mt:1.5,p:1.8,borderRadius:2,borderColor:'#e3eaf3'}}>
              <Box sx={{display:'flex',justifyContent:'space-between',gap:2,alignItems:'center',flexWrap:'wrap'}}>
                <Box>
                  <Typography fontWeight={900}>Postęp amortyzacji</Typography>
                  <Typography variant="body2" color="text.secondary">{postep.toFixed(1)}% wartości początkowej zostało zamortyzowane.</Typography>
                </Box>
                <Chip size="small" label={zakonczona?'Zakończona':'Aktywna'} sx={{fontWeight:800}}/>
              </Box>
              <LinearProgress variant="determinate" value={postep} sx={{mt:1.3,height:9,borderRadius:10}}/>
            </Paper>

            <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',lg:'1fr 1fr'},gap:1.5,mt:1.5}}>
              <Paper variant="outlined" sx={{p:1.8,borderRadius:2,borderColor:'#e3eaf3'}}>
                <Typography fontWeight={900} sx={{mb:1}}>Parametry planu</Typography>
                {[
                  ['Metoda',metoda],
                  ['Stawka',planAmortyzacji.metoda_kod==='JEDNORAZOWA'?'—':`${Number(planAmortyzacji.stawka_zastosowana_proc||0)}%`],
                  ['Współczynnik',planAmortyzacji.metoda_kod==='DEGRESYWNA'?Number(planAmortyzacji.wspolczynnik_degresywny||2):'—'],
                  ['Data wprowadzenia',String(planAmortyzacji.data_wprowadzenia||'—').slice(0,10)]
                ].map(([l,v])=><Box key={l} sx={{display:'flex',justifyContent:'space-between',gap:2,py:.65,borderBottom:'1px solid #f0f3f7'}}>
                  <Typography variant="body2" color="text.secondary">{l}</Typography><Typography variant="body2" fontWeight={800}>{v}</Typography>
                </Box>)}
              </Paper>

              <Paper variant="outlined" sx={{p:1.8,borderRadius:2,borderColor:'#e3eaf3'}}>
                <Typography fontWeight={900} sx={{mb:1}}>Okres amortyzacji</Typography>
                {[
                  ['Rozpoczęcie',start],
                  ['Planowane zakończenie',koniec],
                  ['Odpisy zrealizowane',historia.length],
                  ['Odpisy pozostałe',przyszle.length]
                ].map(([l,v])=><Box key={l} sx={{display:'flex',justifyContent:'space-between',gap:2,py:.65,borderBottom:'1px solid #f0f3f7'}}>
                  <Typography variant="body2" color="text.secondary">{l}</Typography><Typography variant="body2" fontWeight={800}>{v}</Typography>
                </Box>)}
                {stanAmortyzacji?.nastepny_odpis&&<Box sx={{mt:1,p:1.1,bgcolor:'#f7faff',borderRadius:1.5}}>
                  <Typography variant="caption" color="text.secondary">Następny odpis</Typography>
                  <Typography variant="body2" fontWeight={850}>{String(stanAmortyzacji.nastepny_odpis.data_odpisu).slice(0,10)} · {money(stanAmortyzacji.nastepny_odpis.kwota_odpisu)}</Typography>
                </Box>}
              </Paper>
            </Box>

            {planAmortyzacji.uwagi&&<Paper variant="outlined" sx={{mt:1.5,p:1.6,borderRadius:2,borderColor:'#e3eaf3'}}>
              <Typography variant="caption" color="text.secondary">Uwagi do amortyzacji</Typography>
              <Typography variant="body2" sx={{whiteSpace:'pre-wrap',mt:.4}}>{planAmortyzacji.uwagi}</Typography>
            </Paper>}

            <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',lg:'250px minmax(0,1fr)'},gap:1.5,mt:2,alignItems:'start'}}>
              <Paper variant="outlined" sx={{p:1.4,borderRadius:2,borderColor:'#e3eaf3'}}>
                <Typography fontWeight={900} sx={{mb:.4}}>Wartość w czasie</Typography>
                <Typography variant="caption" color="text.secondary">Szybki podgląd wartości netto</Typography>
                <Box sx={{mt:.5}}>
                  <MiniWykresAmortyzacji harmonogram={harmonogramAmortyzacji} wartoscPoczatkowa={wartosc}/>
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{p:{xs:1.2,sm:1.5},borderRadius:2,borderColor:'#e3eaf3',minWidth:0}}>
                <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:1.5,flexWrap:'wrap',mb:1.1}}>
                  <Box>
                    <Typography fontWeight={900}>Historia i harmonogram miesiąc po miesiącu</Typography>
                    <Typography variant="body2" color="text.secondary">Odpisy historyczne i przyszły plan.</Typography>
                  </Box>
                  <Box sx={{display:'flex',gap:.7,flexWrap:'wrap'}}>
                    <Button size="small" variant={widokAmortyzacji==='HISTORIA'?'contained':'text'} onClick={()=>{setWidokAmortyzacji('HISTORIA');setStronaAmortyzacji(1);}}>Do dziś ({historia.length})</Button>
                    <Button size="small" variant={widokAmortyzacji==='PRZYSZLE'?'contained':'text'} onClick={()=>{setWidokAmortyzacji('PRZYSZLE');setStronaAmortyzacji(1);}}>Przyszłe ({przyszle.length})</Button>
                  </Box>
                </Box>

              {widoczne.length===0?<Typography color="text.secondary" sx={{py:3,textAlign:'center'}}>Brak odpisów w tej części harmonogramu.</Typography>:<>
                <Box sx={{display:{xs:'none',md:'block'}}}>
                  <Box sx={{display:'grid',gridTemplateColumns:'90px 1fr 1fr 1fr 115px',gap:1,p:1,bgcolor:'#f8fafc',borderRadius:1.5,fontSize:12,fontWeight:850}}>
                    <span>Miesiąc</span><span>Odpis</span><span>Umorzenie</span><span>Wartość netto</span><span>Metoda</span>
                  </Box>
                  {widoczne.map(h=><Box key={h.id_pozycji||h.lp} sx={{display:'grid',gridTemplateColumns:'90px 1fr 1fr 1fr 115px',gap:1,p:1,borderBottom:'1px solid #edf1f6',alignItems:'center'}}>
                    <Typography variant="body2">{String(h.miesiac).padStart(2,'0')}.{h.rok}</Typography>
                    <Typography variant="body2">{money(h.kwota_odpisu)}</Typography>
                    <Typography variant="body2">{money(h.umorzenie_narastajaco)}</Typography>
                    <Typography variant="body2" fontWeight={850}>{money(h.wartosc_netto)}</Typography>
                    <Chip size="small" label={({LINIOWA:'Liniowa',DEGRESYWNA:'Degresywna',JEDNORAZOWA:'Jednorazowa'}[h.metoda_etapu]||h.metoda_etapu)} sx={{justifySelf:'start'}}/>
                  </Box>)}
                </Box>

                <Box sx={{display:{xs:'grid',md:'none'},gap:1}}>
                  {widoczne.map(h=><Paper key={h.id_pozycji||h.lp} variant="outlined" sx={{p:1.2,borderRadius:1.7}}>
                    <Box sx={{display:'flex',justifyContent:'space-between',gap:1}}><Typography fontWeight={900}>{String(h.miesiac).padStart(2,'0')}.{h.rok}</Typography><Chip size="small" label={({LINIOWA:'Liniowa',DEGRESYWNA:'Degresywna',JEDNORAZOWA:'Jednorazowa'}[h.metoda_etapu]||h.metoda_etapu)}/></Box>
                    <Box sx={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:.8,mt:.8}}>
                      {[['Odpis',money(h.kwota_odpisu)],['Umorzenie',money(h.umorzenie_narastajaco)],['Netto',money(h.wartosc_netto)]].map(([l,v])=><Box key={l} sx={{p:.8,bgcolor:'#f8fafc',borderRadius:1.2}}><Typography variant="caption" color="text.secondary">{l}</Typography><Typography variant="body2" fontWeight={850}>{v}</Typography></Box>)}
                    </Box>
                  </Paper>)}
                </Box>
              </>}

              {dane.length>NA_STRONE&&<Box sx={{display:'flex',justifyContent:'flex-end',pt:1.5}}><Pagination size="small" count={strony} page={page} onChange={(_,p)=>setStronaAmortyzacji(p)} color="primary"/></Box>}
              </Paper>
            </Box>
          </>;
        })()}
      </Box>}
      </Box>
    </Paper>

    <Dialog open={open} onClose={()=>setOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>Przesunięcie środka</DialogTitle>
      <DialogContent sx={{ pt:'12px !important', display:'grid', gap:2 }}>
        <TextField select label="Nowa lokalizacja" value={ruch.id_lokalizacji_do} onChange={e=>setRuch(r=>({...r,id_lokalizacji_do:e.target.value}))}>
          <MenuItem value="">Brak</MenuItem>{lokalizacje.map(x=><MenuItem key={x.id_lokalizacji} value={x.id_lokalizacji}>{x.nazwa}</MenuItem>)}
        </TextField>
        <TextField select label="Nowy użytkownik" value={ruch.id_pracownika_do} onChange={e=>setRuch(r=>({...r,id_pracownika_do:e.target.value}))}>
          <MenuItem value="">Brak</MenuItem>{pracownicy.map(x=><MenuItem key={x.id_pracownika} value={x.id_pracownika}>{x.imie} {x.nazwisko}</MenuItem>)}
        </TextField>
        <TextField select label="Powód" value={ruch.powod} onChange={e=>setRuch(r=>({...r,powod:e.target.value}))}>
          {['PRZENIESIENIE','PRZEKAZANIE','ZWROT','SERWIS','MAGAZYN','LIKWIDACJA','INNE'].map(x=><MenuItem key={x} value={x}>{x}</MenuItem>)}
        </TextField>
        <TextField multiline minRows={3} label="Notatka do przesunięcia" value={ruch.komentarz} onChange={e=>setRuch(r=>({...r,komentarz:e.target.value}))}
          helperText="Opcjonalna informacja, która będzie widoczna w historii przesunięć."/>
      </DialogContent>
      <DialogActions><Button onClick={()=>setOpen(false)}>Anuluj</Button><Button variant="contained" onClick={przesun}>Zapisz</Button></DialogActions>
    </Dialog>

    <Dialog open={openUsun} onClose={()=>setOpenUsun(false)} fullWidth maxWidth="sm">
      <DialogTitle>Wniosek o trwałe usunięcie</DialogTitle>
      <DialogContent sx={{pt:'12px !important'}}>
        <Alert severity="warning" sx={{mb:2}}>Środek nie zostanie usunięty od razu. Wniosek musi zatwierdzić administrator.</Alert>
        {bladUsuniecia && <Alert severity="error" sx={{mb:2}}>{bladUsuniecia}</Alert>}
        <TextField fullWidth multiline minRows={4} label="Powód usunięcia" value={powodUsuniecia} onChange={e=>setPowodUsuniecia(e.target.value)} />
      </DialogContent>
      <DialogActions><Button onClick={()=>setOpenUsun(false)}>Anuluj</Button><Button color="error" variant="contained" onClick={poprosOUsuniecie}>Wyślij wniosek</Button></DialogActions>
    </Dialog>
  </>;
}
